import React, { useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform, ScrollView } from 'react-native';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { getPaymentMethodLabel, hasPaymentMethodCardExpired } from '@/utils/residents/paymentUtils';
import { usePaymentMethodSelection } from '@/features/payments/hooks/usePaymentValidation';
import { Radio } from '@/components/common/Radio';
import { getStableMethodKey } from '@/utils/residents/paymentMethods';

interface PaymentMethodSelectorProps {
  paymentMethods: any[];
  selectedMethod?: any;
  onSelectMethod: (method: any) => void;
  onManagePaymentMethods: () => void;
  isLoading?: boolean;
  showChangeButton?: boolean;
  maxHeight?: number;
}

export const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  paymentMethods,
  selectedMethod,
  onSelectMethod,
  onManagePaymentMethods,
  isLoading = false,
  showChangeButton = true,
  maxHeight,
}) => {
  // Use shared selection hook to classify methods consistently across app
  const {
    availableMethods,
    expiredMethods,
    blockedMethods,
    hasValidMethods,
    allMethodsExpired,
  } = usePaymentMethodSelection(paymentMethods, selectedMethod?.id || selectedMethod?.sourceId);
  

  if (isLoading) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>Payment Method</Text>
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Loading payment methods...</Text>
        </View>
      </View>
    );
  }

  if (!hasValidMethods) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>Payment Method</Text>
        <View style={styles.noMethodsContainer}>
          <Text style={styles.noMethodsText}>
            {allMethodsExpired 
              ? 'All payment methods have expired' 
              : 'No payment methods available'}
          </Text>
          <TouchableOpacity style={styles.addMethodButton} onPress={onManagePaymentMethods}>
            <Text style={styles.addMethodButtonText}>Add Payment Method</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  const Body = (
    <>
      {/* Selected Method Display */}
      {selectedMethod && (
        <View style={styles.selectedMethodContainer}>
          <View style={styles.methodInfo}>
            <Text style={styles.methodText}>
              {getPaymentMethodLabel(selectedMethod)}
            </Text>
            {hasPaymentMethodCardExpired(selectedMethod.expiry) && (
              <Text style={styles.expiredText}>EXPIRED</Text>
            )}
            {selectedMethod.blockedAt && (
              <Text style={styles.blockedText}>BLOCKED</Text>
            )}
            {selectedMethod.isDefault && !hasPaymentMethodCardExpired(selectedMethod.expiry) && (
              <Text style={styles.defaultText}>DEFAULT</Text>
            )}
          </View>
          {showChangeButton && (
            <TouchableOpacity onPress={onManagePaymentMethods}>
              <Text style={styles.changeLink}>Change</Text>
            </TouchableOpacity>
          )}
        </View>
      )}
      {/* Method Selection List (when multiple methods available) */}
      {availableMethods.length > 1 && (
        <View style={styles.methodsList}>
          {availableMethods.map((method, index) => (
            <PaymentMethodItem
              key={getStableMethodKey(method)}
              method={method}
              isSelected={selectedMethod?.id === method.id}
              onSelect={() => onSelectMethod(method)}
            />
          ))}
        </View>
      )}
      {/* Warnings for expired/blocked methods */}
      {expiredMethods.length > 0 && (
        <View style={styles.warningContainer}>
          <View style={styles.warningIcon}>
            {(() => {
              const Icon = (RESIDENTS_SVG_COMPONENTS as any).EXCLAMATION_TRIANGLE;
              return Icon ? <Icon width={16} height={16} color="#F59E0B" /> : null;
            })()}
          </View>
          <Text style={styles.warningText}>
            {expiredMethods.length} payment method{expiredMethods.length > 1 ? 's' : ''} expired
          </Text>
        </View>
      )}
      {blockedMethods.length > 0 && (
        <View style={styles.warningContainer}>
          <View style={styles.warningIcon}>
            {(() => {
              const Icon = (RESIDENTS_SVG_COMPONENTS as any).EXCLAMATION_TRIANGLE;
              return Icon ? <Icon width={16} height={16} color="#EF4444" /> : null;
            })()}
          </View>
          <Text style={styles.warningText}>
            {blockedMethods.length} payment method{blockedMethods.length > 1 ? 's' : ''} blocked
          </Text>
        </View>
      )}
    </>
  );

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Payment Method</Text>
      {maxHeight ? (
        <ScrollView style={[styles.scrollArea, { maxHeight }]} contentContainerStyle={styles.scrollAreaContent}>
          {Body}
        </ScrollView>
      ) : (
        Body
      )}
    </View>
  );
};

interface PaymentMethodItemProps {
  method: any;
  isSelected: boolean;
  onSelect: () => void;
}

const PaymentMethodItem: React.FC<PaymentMethodItemProps> = ({
  method,
  isSelected,
  onSelect,
}) => {
  const isExpired = hasPaymentMethodCardExpired(method.expiry);
  const isBlocked = Boolean(method.blockedAt);
  const isDisabled = isExpired || isBlocked;

  return (
    <TouchableOpacity
      style={[
        styles.methodItem,
        isSelected && styles.methodItemSelected,
        isDisabled && styles.methodItemDisabled,
      ]}
      onPress={onSelect}
      disabled={isDisabled}
    >
        <View style={styles.methodItemContent}>
        <Radio selected={isSelected} disabled={isDisabled} />
        
        <View style={styles.methodDetails}>
          <Text style={[
            styles.methodLabel,
            isDisabled && styles.methodLabelDisabled,
          ]}>
            {getPaymentMethodLabel(method)}
          </Text>
          
          <View style={styles.methodStatus}>
            {method.isDefault && !isDisabled && (
              <Text style={styles.defaultBadge}>Default</Text>
            )}
            {isExpired && (
              <Text style={styles.expiredBadge}>Expired</Text>
            )}
            {isBlocked && (
              <Text style={styles.blockedBadge}>Blocked</Text>
            )}
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    marginVertical: 8,
  },
  title: {
    fontSize: 16,
    fontWeight: '700',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    marginBottom: 8,
  },
  loadingContainer: {
    padding: 16,
    alignItems: 'center',
  },
  loadingText: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    fontSize: 14,
  },
  noMethodsContainer: {
    padding: 16,
    alignItems: 'center',
  },
  noMethodsText: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    fontSize: 14,
    marginBottom: 12,
    textAlign: 'center',
  },
  addMethodButton: {
    backgroundColor: RESIDENTS_DESKTOP_COLORS.BUTTON_PRIMARY,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  addMethodButtonText: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontSize: 14,
    fontWeight: '600',
  },
  selectedMethodContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  methodInfo: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  methodText: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontSize: 16,
    fontWeight: '600',
    marginRight: 8,
  },
  expiredText: {
    color: '#EF4444',
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 8,
  },
  blockedText: {
    color: '#EF4444',
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 8,
  },
  defaultText: {
    color: '#10B981',
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 8,
  },
  changeLink: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_SECONDARY,
    fontSize: 14,
    fontWeight: '600',
  },
  methodsList: {
    marginTop: 8,
  },
  scrollArea: {
    marginTop: 8,
  },
  scrollAreaContent: {
    paddingBottom: 4,
  },
  methodItem: {
    paddingVertical: 12,
    paddingHorizontal: 4,
    borderRadius: 8,
    marginBottom: 4,
  },
  methodItemSelected: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
  methodItemDisabled: {
    opacity: 0.5,
  },
  methodItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  methodDetails: {
    flex: 1,
  },
  methodLabel: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontSize: 14,
    fontWeight: '500',
  },
  methodLabelDisabled: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
  },
  methodStatus: {
    flexDirection: 'row',
    marginTop: 2,
  },
  defaultBadge: {
    color: '#10B981',
    fontSize: 12,
    fontWeight: '500',
  },
  expiredBadge: {
    color: '#EF4444',
    fontSize: 12,
    fontWeight: '500',
  },
  blockedBadge: {
    color: '#EF4444',
    fontSize: 12,
    fontWeight: '500',
  },
  warningContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
    padding: 8,
    backgroundColor: 'rgba(245, 158, 11, 0.1)',
    borderRadius: 6,
  },
  warningIcon: {
    marginRight: 8,
  },
  warningText: {
    color: '#F59E0B',
    fontSize: 12,
    flex: 1,
  },
});

export default PaymentMethodSelector;
